import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { reportsAPI } from '../lib/api';
import { formatCurrency, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Filter,
    RefreshCw,
    Calculator,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Package,
    Users,
    FileSpreadsheet,
    ChevronDown,
    ChevronUp,
    Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

// Quick date range presets
const datePresets = [
    { label: "Aujourd'hui", getValue: () => {
        const today = new Date().toISOString().split('T')[0];
        return { start: today, end: today };
    }},
    { label: 'Cette semaine', getValue: () => {
        const now = new Date();
        const start = new Date(now);
        start.setDate(now.getDate() - now.getDay());
        const end = new Date(now);
        return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'Ce mois', getValue: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now);
        return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
    { label: 'Mois dernier', getValue: () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const end = new Date(now.getFullYear(), now.getMonth(), 0);
        return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
    }},
];

export default function CommissionReport() {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState({ orders: [], totals: {} });
    const [vendors, setVendors] = useState([]);
    const [selectedOrders, setSelectedOrders] = useState([]);
    const [expandedOrder, setExpandedOrder] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        vendor_id: '',
        start_date: '',
        end_date: '',
        status: 'livre' // Default to delivered
    });
    
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'office_agent';
    
    // Fetch vendors list (for admin filter)
    useEffect(() => {
        const fetchVendors = async () => {
            try {
                const response = await reportsAPI.getVendors();
                setVendors(response.data.vendors || []);
            } catch (error) {
                console.error('Error fetching vendors:', error);
            }
        };
        fetchVendors();
    }, []);
    
    // Fetch report data
    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.vendor_id) params.vendor_id = filters.vendor_id;
            if (filters.start_date) params.start_date = filters.start_date;
            if (filters.end_date) params.end_date = filters.end_date;
            if (filters.status) params.status = filters.status;
            
            const response = await reportsAPI.getCommissions(params);
            setReportData(response.data);
            setSelectedOrders(response.data.orders.map(o => o.id)); // Select all by default
        } catch (error) {
            console.error('Error fetching report:', error);
            toast.error('Erreur lors du chargement du rapport');
        } finally {
            setLoading(false);
        }
    }, [filters]);
    
    useEffect(() => {
        fetchReport();
    }, [fetchReport]);
    
    // Calculate totals for selected orders only
    const selectedTotals = React.useMemo(() => {
        const selected = reportData.orders.filter(o => selectedOrders.includes(o.id));
        return {
            total_orders: selected.length,
            total_prix_vente: selected.reduce((sum, o) => sum + (o.prix_vente || 0), 0),
            total_prix_achat: selected.reduce((sum, o) => sum + (o.prix_achat || 0), 0),
            total_frais_livraison: selected.reduce((sum, o) => sum + (o.frais_livraison || 0), 0),
            total_client_paid: selected.reduce((sum, o) => sum + (o.client_total || o.prix_vente + o.frais_livraison || 0), 0),
            total_pa_marketing_fee: selected.reduce((sum, o) => sum + (o.pa_marketing_fee_5_percent || 0), 0),
            total_gross_profit: selected.reduce((sum, o) => sum + (o.gross_profit || 0), 0),
            total_profit_to_share: selected.reduce((sum, o) => sum + (o.profit_to_share || o.net_profit || 0), 0),
            total_vendor_commission: selected.reduce((sum, o) => sum + (o.vendor_commission || 0), 0),
            total_pa_commission: selected.reduce((sum, o) => sum + (o.pa_commission || 0), 0),
        };
    }, [reportData.orders, selectedOrders]);
    
    // Apply date preset
    const applyDatePreset = (preset) => {
        const { start, end } = preset.getValue();
        setFilters(prev => ({ ...prev, start_date: start, end_date: end }));
    };
    
    // Toggle order selection
    const toggleOrderSelection = (orderId) => {
        setSelectedOrders(prev => 
            prev.includes(orderId) 
                ? prev.filter(id => id !== orderId)
                : [...prev, orderId]
        );
    };
    
    // Select/deselect all
    const toggleSelectAll = () => {
        if (selectedOrders.length === reportData.orders.length) {
            setSelectedOrders([]);
        } else {
            setSelectedOrders(reportData.orders.map(o => o.id));
        }
    };
    
    // Export to Excel - New Simplified Model
    const exportToExcel = () => {
        const selected = reportData.orders.filter(o => selectedOrders.includes(o.id));
        
        const exportData = selected.map(order => ({
            'N° Commande': order.n_commande,
            'Date': order.date_commande?.substring(0, 10),
            'Client': order.full_name,
            'Produit': order.produit,
            'Statut': order.status,
            'Prix Vente (DA)': order.prix_vente,
            'Frais Livraison (DA)': order.frais_livraison,
            'Total Client (DA)': order.client_total || (order.prix_vente + order.frais_livraison),
            'Prix Achat (DA)': order.prix_achat,
            '5% Marketing PA (DA)': order.pa_marketing_fee_5_percent || (order.prix_vente * 0.05),
            'Profit à Partager (DA)': order.profit_to_share || order.net_profit,
            'Vendeur Reçoit (DA)': order.vendor_commission,
            'PA Garde (DA)': order.pa_commission,
            'Vendeur': order.assigned_vendeur_name || order.claimed_by_vendeur_name || '-'
        }));
        
        // Add totals row
        exportData.push({
            'N° Commande': 'TOTAUX',
            'Date': '',
            'Client': '',
            'Produit': `${selectedTotals.total_orders} commandes`,
            'Statut': '',
            'Prix Vente (DA)': selectedTotals.total_prix_vente,
            'Frais Livraison (DA)': selectedTotals.total_frais_livraison,
            'Total Client (DA)': selectedTotals.total_client_paid,
            'Prix Achat (DA)': selectedTotals.total_prix_achat,
            '5% Marketing PA (DA)': selectedTotals.total_pa_marketing_fee,
            'Profit à Partager (DA)': selectedTotals.total_profit_to_share,
            'Vendeur Reçoit (DA)': selectedTotals.total_vendor_commission,
            'PA Garde (DA)': selectedTotals.total_pa_commission,
            'Vendeur': ''
        });
        
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rapport Commissions');
        
        const vendorName = filters.vendor_id 
            ? vendors.find(v => v.id === filters.vendor_id)?.name || 'vendeur'
            : 'tous';
        const fileName = `rapport_commissions_${vendorName}_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        XLSX.writeFile(wb, fileName);
        toast.success('Export Excel téléchargé');
    };
    
    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold">Rapport de Commissions</h1>
                        <p className="text-muted-foreground">
                            Calculez et exportez les commissions des vendeurs
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchReport} disabled={loading}>
                            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                            Actualiser
                        </Button>
                        <Button onClick={exportToExcel} disabled={selectedOrders.length === 0}>
                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                            Export Excel
                        </Button>
                    </div>
                </div>
                
                {/* Filters */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Filter className="w-5 h-5" />
                            Filtres
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            {/* Vendor filter (admin only) */}
                            {isAdmin && (
                                <div className="space-y-2">
                                    <Label>Vendeur</Label>
                                    <Select
                                        value={filters.vendor_id || "all"}
                                        onValueChange={(value) => setFilters(prev => ({ ...prev, vendor_id: value === "all" ? "" : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Tous les vendeurs" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">Tous les vendeurs</SelectItem>
                                            {vendors.map((vendor) => (
                                                <SelectItem key={vendor.id} value={vendor.id}>
                                                    {vendor.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            
                            {/* Date range */}
                            <div className="space-y-2">
                                <Label>Date début</Label>
                                <Input
                                    type="date"
                                    value={filters.start_date}
                                    onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Date fin</Label>
                                <Input
                                    type="date"
                                    value={filters.end_date}
                                    onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                />
                            </div>
                            
                            {/* Status filter */}
                            <div className="space-y-2">
                                <Label>Statut</Label>
                                <Select
                                    value={filters.status}
                                    onValueChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="livre">Livrées uniquement</SelectItem>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        <SelectItem value="confirme">Confirmées</SelectItem>
                                        <SelectItem value="expedie">Expédiées</SelectItem>
                                        <SelectItem value="annule">Annulées</SelectItem>
                                        <SelectItem value="retourne">Retournées</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            {/* Quick presets */}
                            <div className="space-y-2">
                                <Label>Période rapide</Label>
                                <div className="flex flex-wrap gap-1">
                                    {datePresets.map((preset, idx) => (
                                        <Button
                                            key={idx}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => applyDatePreset(preset)}
                                            className="text-xs"
                                        >
                                            {preset.label}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Summary Cards - New Simplified Model */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                <Package className="w-4 h-4" />
                                Commandes
                            </div>
                            <p className="text-2xl font-bold mt-1">{selectedTotals.total_orders}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                <DollarSign className="w-4 h-4" />
                                Total Client
                            </div>
                            <p className="text-2xl font-bold mt-1">{formatCurrency(selectedTotals.total_client_paid)}</p>
                            <p className="text-xs text-muted-foreground">
                                Ventes: {formatCurrency(selectedTotals.total_prix_vente)} + Livraison: {formatCurrency(selectedTotals.total_frais_livraison)}
                            </p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm">
                                <TrendingUp className="w-4 h-4" />
                                Profit Brut
                            </div>
                            <p className="text-2xl font-bold mt-1 text-green-600">{formatCurrency(selectedTotals.total_gross_profit)}</p>
                            <p className="text-xs text-muted-foreground">Ventes - Prix Achat</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-orange-600 text-sm">
                                <TrendingDown className="w-4 h-4" />
                                5% Marketing PA
                            </div>
                            <p className="text-2xl font-bold mt-1 text-orange-500">{formatCurrency(selectedTotals.total_pa_marketing_fee)}</p>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Commission Split Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-blue-700 text-sm font-medium">
                                <Users className="w-4 h-4" />
                                💰 VENDEUR REÇOIT
                            </div>
                            <p className="text-3xl font-bold mt-2 text-blue-700">{formatCurrency(selectedTotals.total_vendor_commission)}</p>
                            <div className="text-xs text-blue-600 mt-2 space-y-1">
                                <p>Prix Achat (remboursé): {formatCurrency(selectedTotals.total_prix_achat)}</p>
                                <p>Livraison (pour livreur): {formatCurrency(selectedTotals.total_frais_livraison)}</p>
                                <p>50% Profit: {formatCurrency(selectedTotals.total_profit_to_share / 2)}</p>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-2 text-purple-700 text-sm font-medium">
                                <Calculator className="w-4 h-4" />
                                🏢 PARFUM ALGÉRIE GARDE
                            </div>
                            <p className="text-3xl font-bold mt-2 text-purple-700">{formatCurrency(selectedTotals.total_pa_commission)}</p>
                            <div className="text-xs text-purple-600 mt-2 space-y-1">
                                <p>5% Marketing: {formatCurrency(selectedTotals.total_pa_marketing_fee)}</p>
                                <p>50% Profit: {formatCurrency(selectedTotals.total_profit_to_share / 2)}</p>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Orders Table */}
                <Card>
                    <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-lg">
                                Détail des commandes ({selectedOrders.length}/{reportData.orders.length} sélectionnées)
                            </CardTitle>
                            <Button variant="outline" size="sm" onClick={toggleSelectAll}>
                                {selectedOrders.length === reportData.orders.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                            </div>
                        ) : reportData.orders.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                Aucune commande trouvée avec ces filtres
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-10">
                                                <Checkbox
                                                    checked={selectedOrders.length === reportData.orders.length}
                                                    onCheckedChange={toggleSelectAll}
                                                />
                                            </TableHead>
                                            <TableHead>N° Commande</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Client / Produit</TableHead>
                                            <TableHead className="text-right">Prix Vente</TableHead>
                                            <TableHead className="text-right">Livraison</TableHead>
                                            <TableHead className="text-right">Prix Achat</TableHead>
                                            <TableHead className="text-right">5% PA</TableHead>
                                            <TableHead className="text-right text-blue-600">Vendeur</TableHead>
                                            <TableHead className="text-right text-purple-600">PA</TableHead>
                                            <TableHead>Détails</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {reportData.orders.map((order) => (
                                            <React.Fragment key={order.id}>
                                                <TableRow className={cn(
                                                    !selectedOrders.includes(order.id) && "opacity-50",
                                                    expandedOrder === order.id && "bg-muted/50"
                                                )}>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedOrders.includes(order.id)}
                                                            onCheckedChange={() => toggleOrderSelection(order.id)}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="font-medium">{order.n_commande}</TableCell>
                                                    <TableCell className="text-sm">{order.date_commande?.substring(0, 10)}</TableCell>
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{order.full_name}</p>
                                                            <p className="text-xs text-muted-foreground truncate max-w-[150px]">{order.produit}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium">{formatCurrency(order.prix_vente)}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(order.frais_livraison)}</TableCell>
                                                    <TableCell className="text-right">{formatCurrency(order.prix_achat)}</TableCell>
                                                    <TableCell className="text-right text-orange-500">
                                                        {formatCurrency(order.pa_marketing_fee_5_percent || order.prix_vente * 0.05)}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <span className="font-bold text-blue-600">{formatCurrency(order.vendor_commission)}</span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <span className="font-bold text-purple-600">{formatCurrency(order.pa_commission)}</span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8"
                                                            onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                                                        >
                                                            {expandedOrder === order.id ? (
                                                                <ChevronUp className="w-4 h-4" />
                                                            ) : (
                                                                <ChevronDown className="w-4 h-4" />
                                                            )}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                
                                                {/* Expanded row with commission breakdown */}
                                                {expandedOrder === order.id && (
                                                    <TableRow>
                                                        <TableCell colSpan={11} className="bg-muted/30">
                                                            <div className="p-4">
                                                                {/* Commission calculation breakdown - New Simplified Model */}
                                                                <div className="bg-white p-4 rounded border">
                                                                    <h4 className="font-medium mb-3 text-lg">📊 Détail du Calcul - Commande {order.n_commande}</h4>
                                                                    
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                        {/* Left side - Calculation */}
                                                                        <div className="space-y-3">
                                                                            <div className="p-3 bg-gray-50 rounded">
                                                                                <p className="text-sm text-muted-foreground">💳 Client a payé</p>
                                                                                <p className="text-xl font-bold">{formatCurrency(order.client_total || (order.prix_vente + order.frais_livraison))}</p>
                                                                                <p className="text-xs text-muted-foreground">
                                                                                    (Prix: {formatCurrency(order.prix_vente)} + Livraison: {formatCurrency(order.frais_livraison)})
                                                                                </p>
                                                                            </div>
                                                                            
                                                                            <div className="space-y-2 text-sm">
                                                                                <div className="flex justify-between">
                                                                                    <span>Prix de vente</span>
                                                                                    <span className="font-medium">{formatCurrency(order.prix_vente)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between text-red-600">
                                                                                    <span>- Prix d&apos;achat</span>
                                                                                    <span>{formatCurrency(order.prix_achat)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between text-orange-600">
                                                                                    <span>- 5% Marketing PA</span>
                                                                                    <span>{formatCurrency(order.pa_marketing_fee_5_percent || order.prix_vente * 0.05)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between font-bold border-t pt-2">
                                                                                    <span>= Profit à partager</span>
                                                                                    <span className="text-green-600">{formatCurrency(order.profit_to_share || order.net_profit)}</span>
                                                                                </div>
                                                                                <div className="flex justify-between text-muted-foreground">
                                                                                    <span>÷ 2 (50/50)</span>
                                                                                    <span>{formatCurrency((order.profit_to_share || order.net_profit) / 2)} chacun</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        
                                                                        {/* Right side - Results */}
                                                                        <div className="space-y-3">
                                                                            <div className="p-3 bg-blue-50 rounded border border-blue-200">
                                                                                <p className="text-sm text-blue-700 font-medium">💰 VENDEUR REÇOIT</p>
                                                                                <p className="text-2xl font-bold text-blue-700">{formatCurrency(order.vendor_commission)}</p>
                                                                                <div className="text-xs text-blue-600 mt-2 space-y-1">
                                                                                    <p>• Prix achat (remboursé): {formatCurrency(order.prix_achat)}</p>
                                                                                    <p>• Livraison (pour livreur): {formatCurrency(order.frais_livraison)}</p>
                                                                                    <p>• 50% profit: {formatCurrency((order.profit_to_share || order.net_profit) / 2)}</p>
                                                                                </div>
                                                                            </div>
                                                                            
                                                                            <div className="p-3 bg-purple-50 rounded border border-purple-200">
                                                                                <p className="text-sm text-purple-700 font-medium">🏢 PARFUM ALGÉRIE GARDE</p>
                                                                                <p className="text-2xl font-bold text-purple-700">{formatCurrency(order.pa_commission)}</p>
                                                                                <div className="text-xs text-purple-600 mt-2 space-y-1">
                                                                                    <p>• 5% Marketing: {formatCurrency(order.pa_marketing_fee_5_percent || order.prix_vente * 0.05)}</p>
                                                                                    <p>• 50% profit: {formatCurrency((order.profit_to_share || order.net_profit) / 2)}</p>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
